import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { GitBranch, Eye, EyeOff, Mail, Lock, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, loginWithToken, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if user is already logged in as staff - redirect them
  useEffect(() => {
    if (user && ["vendedor", "admin", "asesor", "legal", "oficial"].includes(user.role)) {
      // Staff users should go to their respective dashboards
      if (user.role === "vendedor") {
        navigate("/vendedor", { replace: true });
      } else if (user.role === "oficial") {
        navigate("/oficial", { replace: true });
      } else {
        navigate("/admin", { replace: true });
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Por favor completa todos los campos");
      return;
    }

    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`¡Bienvenido ${user.name}!`);
      // Route based on user role
      let targetPath = "/dashboard";
      if (["admin", "legal", "asesor"].includes(user.role)) {
        targetPath = "/admin";
      } else if (user.role === "vendedor") {
        targetPath = "/vendedor";
      } else if (user.role === "oficial") {
        targetPath = "/oficial";
      }
      setTimeout(() => {
        navigate(targetPath, { replace: true });
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-50 flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Back Link */}
          <Link to="/" className="inline-flex items-center gap-2 text-navy-500 hover:text-navy-700 mb-8 text-sm">
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <GitBranch className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-2xl font-bold text-navy-900">Divídelo</span>
          </Link>

          <h1 className="font-heading text-3xl font-bold text-navy-900 mb-2">
            Accede a tu cuenta
          </h1>
          <p className="text-navy-600 mb-8">
            Ingresa tus credenciales para continuar con tu proceso
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-navy-700 font-medium">Correo electrónico</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 h-12 bg-white border-navy-200 focus:border-emerald-500 focus:ring-emerald-500"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-navy-700 font-medium">Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 pr-12 h-12 bg-white border-navy-200 focus:border-emerald-500 focus:ring-emerald-500"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-navy-400 hover:text-navy-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-lg shadow-emerald-500/25"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                "Iniciar Sesión"
              )}
            </Button>
          </form>

          <div className="text-center mt-6">
            <Link to="/forgot-password" className="text-navy-500 hover:text-emerald-600 text-sm font-medium transition-colors">
              ¿Olvidaste tu contraseña?
            </Link>
          </div>

          <div className="text-center text-sm text-navy-500 mt-6">
            <p>¿No tienes cuenta? Contacta a tu asesor comercial.</p>
          </div>
        </motion.div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:flex lg:flex-1 relative bg-navy-900 items-center justify-center overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:64px_64px]" />
        
        {/* Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-emerald-500 rounded-full blur-[150px] opacity-20" />
        
        {/* Content */}
        <div className="relative z-10 max-w-md text-center px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div className="w-20 h-20 bg-emerald-500/20 rounded-2xl flex items-center justify-center mx-auto mb-8">
              <GitBranch className="w-10 h-10 text-emerald-400" />
            </div>
            
            <h2 className="font-heading text-3xl font-bold text-white mb-4">
              Divide la complejidad.
            </h2>
            <p className="text-navy-300 text-lg mb-8">
              Ordena el proceso. Avanza con claridad.
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4">
                <div className="text-2xl font-bold text-emerald-400">5</div>
                <div className="text-xs text-navy-400">Etapas</div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4">
                <div className="text-2xl font-bold text-emerald-400">24/7</div>
                <div className="text-xs text-navy-400">Soporte</div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4">
                <div className="text-2xl font-bold text-emerald-400">100%</div>
                <div className="text-xs text-navy-400">Seguro</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
